/**
 * Session-scoped cache of verification-engine KYC links, keyed by identity id.
 *
 * Issuing a link (`POST /internal/identities/verification/:identityId/kyc-link`) EXPIRES every previous
 * active link of that identity on the backend, so re-issuing "just to display it" would silently invalidate a
 * link already shared with the client. This cache lets the detail view reuse the link issued during the
 * create flow (or a previous visit) instead of minting a new one. In-memory only — a durable source would be
 * the backend populating `activeKycLink*` on the detail endpoints (stubbed to null on qa today).
 */

/** Engine-issued verification link as the UI consumes it. */
export interface IssuedKycLinkEntry {
  url: string;
  /** ISO timestamp (or null when the engine omitted it). Expired entries are dropped on read. */
  expiresAt: string | null;
}

const issuedLinksByIdentity = new Map<string, IssuedKycLinkEntry>();

function isExpired(entry: IssuedKycLinkEntry): boolean {
  if (!entry.expiresAt) return false;
  const expiresAtMs = new Date(entry.expiresAt).getTime();
  return !Number.isNaN(expiresAtMs) && expiresAtMs <= Date.now();
}

/** Records the link just issued for an identity (replaces any previous entry — the backend expired it). */
export function rememberIssuedKycLink(identityId: string, entry: IssuedKycLinkEntry): void {
  issuedLinksByIdentity.set(identityId, entry);
}

/** Latest link issued this session for an identity, or null when absent or already expired. */
export function getIssuedKycLink(identityId: string): IssuedKycLinkEntry | null {
  const entry = issuedLinksByIdentity.get(identityId);
  if (!entry) return null;
  if (isExpired(entry)) {
    issuedLinksByIdentity.delete(identityId);
    return null;
  }
  return entry;
}
